/**
 * Standalone PDF text/geometry pre-pass (VE-25293).
 *
 * For every page of a document, extracts the page text, per-text-item geometry,
 * and native dimensions WITHOUT rendering the page to a canvas. This is the
 * pre-pass flagged by the long-standing DOC-TODO in DocumentPage: it decouples
 * text extraction from `<Page>` rendering so keyword search, occurrence counts,
 * and the "no searchable text" warnings work across the whole document even
 * though the editor canvas only renders (virtualizes) the pages near the
 * viewport.
 *
 * ENGINE: MuPDF, via the shared render worker (VE-26267). MuPDF's native text
 * extraction is ~170x faster in-browser than pdf.js's `getTextContent` on
 * per-glyph OCR documents (measured on a 1600-page doc: ~1ms/page vs ~170ms/page
 * — pdf.js rebuilds a glyph map per page for OCR fonts that re-declare the font
 * on every page). MuPDF is already loaded for burn-in + rendering, so this adds
 * no new engine, and it returns one compact payload per page RANGE rather than
 * pdf.js's per-page main<->worker round-trips.
 *
 * Text + geometry: each MuPDF glyph run (a character + its top-left-origin box)
 * is fed through the shared `buildPageText` (seam-aware, so per-glyph / split-word
 * pages stay searchable — VE-26267) for the page text, and mapped straight to
 * `items` for highlight geometry. MuPDF already reports boxes in the editor's
 * top-left frame, so — unlike the old pdf.js path — there is no baseline→top-left
 * flip. `dims` come from `page.getBounds()`, and runs arrive in that same
 * page-relative frame — MuPDF normalizes the page box, so no origin correction
 * is needed or wanted here (see the worker's COORDINATE SPACE note, which
 * records the measurement).
 *
 * Also exports `extractSinglePagePrepass` for the render path, so a page that
 * paints before the stream reaches it is indexed by the SAME engine rather than
 * by pdf.js — see that function for why mixing the two silently misplaced
 * redactions.
 *
 * Searchability: a page WITH extractable text is `searchable` here. A text-less
 * page is left PROVISIONALLY `blank` (a non-warning state) — the pre-pass does
 * NOT decode the page to decide scan-vs-blank, because that decode is what made
 * a scanned document's pre-pass block for tens of seconds (VE-27250). Instead
 * the scan-vs-blank verdict is filled in AFTER all pages have streamed: the saga
 * forks a cheap MuPDF raster probe over the text-less pages (populating the
 * document-level "N of K pages unsearchable" count), and a page that scrolls
 * into view refines itself with the exact getOperatorList scan. So a scan reads
 * as `unsearchable` a moment later, and a genuinely blank page never flashes a
 * false warning.
 */
import {
  extractMupdfText,
  MUPDF_PRIORITY_HIGH,
  type MupdfExtractResult,
  type MupdfPageText,
} from '@redact-components/DocumentEditorPage/mupdfRenderWorkerClient';
import { buildPageText, stripItemSeams } from './helpers';
import { timeSpan } from '@redact-components/DocumentEditorPage/mainThreadSpans';

export interface PagePrepassEntry {
  page: number;
  text: string;
  items: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
    length: number;
  }>;
  searchability: 'searchable' | 'unsearchable' | 'blank';
  dims: { width: number; height: number };
}

/** One streamed slice of the pre-pass, plus how far it has gotten. */
export interface PrepassChunk {
  /** Freshly-indexed pages, in ascending page order. */
  chunk: PagePrepassEntry[];
  /** Pages indexed so far (1-based high-water) — drives the progress display. */
  indexed: number;
  /** Total pages in the document. */
  total: number;
}

/**
 * How a pre-pass stream ENDED, as the generator's return value (VE-29686).
 *
 * `failed` when the extractor gave up on a range. It cannot be inferred from
 * the chunks: a failure on the FIRST range yields nothing, exactly like a
 * genuine 0-page document, so the stream has to say which it was.
 */
export interface PrepassEnd {
  failed: boolean;
}

// Pages per extracted range = the streamed-flush unit. A range (not one page per
// message) amortizes the client<->worker round-trip over many pages; coarse
// enough that the per-flush consumer re-render (SET_PAGE_TEXT_BATCH re-renders
// the up-to-`totalPages` thumbnail rail) stays modest, small enough that search
// coverage + progress tick every few hundred ms on a long document (VE-26267).
/**
 * How many pages one text-extract request covers.
 *
 * DO NOT make this adaptive, and do not shrink it — both were tried and made
 * things measurably worse. The motivating reasoning was sound but the premise
 * was wrong: the worker is single-threaded and a dispatched job cannot be
 * preempted, so a range's duration LOOKED like the editor's worst-case
 * interactive latency (measured ~1.07 s per 25-page range on a 2019 i7-10750H,
 * with page renders waiting a mean 1.85 s to be dispatched).
 *
 * But shrinking the range from 25 pages to 4 left that duration essentially
 * unchanged — 1072 ms -> 1032 ms for a SIXTH of the work. The cost is per ROUND
 * TRIP, not per page, and it is not in the worker at all: a response is only
 * delivered when the MAIN THREAD is free, and each chunk hands the main thread
 * ~2500 glyph objects per page to re-map twice, a redux dispatch, an immer pass
 * and a React render. Six times the round trips meant six times those bursts, so
 * page-render DISPATCH went from a 1.85 s wait to 9.3 s (median) — the client's
 * queue lives on the main thread, so a blocked main thread cannot even dispatch.
 *
 * The adaptive controller made it worse still: it timed the whole round trip,
 * i.e. partly the congestion it was itself causing, so it drove straight to its
 * floor — the worst setting available, maximizing round trips.
 *
 * The real fix is to stop shipping an object per glyph (item 11 in
 * plans/plan-doc-editor-load-scheduling.md). Until then a LARGER range is
 * strictly better than a smaller one; 25 is the measured-good value.
 */
const PREPASS_RANGE_PAGES = 25;

// Reserved fallback size for a page whose bounds MuPDF couldn't read (US Letter).
const FALLBACK_PAGE_WIDTH = 612;
const FALLBACK_PAGE_HEIGHT = 792;

const delayMs = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Extract one page range, retrying transient worker failures. The pre-pass
 * shares the MuPDF worker with rendering, so an open-time editor remount can
 * dispose the worker mid-request ("worker disposed"); the next call respawns it,
 * so a few retries ride out the open-time churn instead of abandoning the whole
 * pre-pass on the first hiccup.
 */
async function extractRangeWithRetry(
  pdfUrl: string,
  startPage: number,
  endPage: number,
  priority?: number
): Promise<MupdfExtractResult> {
  const MAX_ATTEMPTS = 5;
  let lastErr: unknown;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    try {
      return await extractMupdfText(pdfUrl, startPage, endPage, priority);
    } catch (err) {
      lastErr = err;
      await delayMs(300 * attempt);
    }
  }
  throw lastErr;
}

/**
 * Turn one page's MuPDF glyph runs into a PagePrepassEntry: page text via the
 * shared seam-aware `buildPageText`, plus per-item highlight geometry. MuPDF
 * boxes are already top-left origin, so items map straight through (no flip).
 */
function toPagePrepassEntry(pt: MupdfPageText): PagePrepassEntry {
  // Shape runs into the pdfjs-like item shape `buildPageText` consumes:
  // transform[4] = x (left), transform[5] = y (top) drives its same-line test —
  // MuPDF geometry is already top-left, so top substitutes for pdf.js's baseline
  // (same-line glyphs share it), and no baseline→top-left flip is needed.
  const pdfLike = pt.runs.map((r) => ({
    str: r.c,
    transform: [0, 0, 0, 0, r.x, r.y],
    width: r.w,
    height: r.h,
  }));
  const text = buildPageText(pdfLike);
  // `length` MUST be the character count `buildPageText` actually contributed,
  // not the raw run's. It strips any real ITEM_SEAM a font emitted (the seam is
  // U+E000, a Private Use Area codepoint MuPDF genuinely returns for CID fonts
  // without ToUnicode and PUA cmaps), so counting the raw char desynchronizes
  // the `offset += length + 1` walk that every offset consumer shares — and the
  // drift is cumulative across the page. Measured: one U+E000 glyph earlier on
  // a page shortened a later surname's box by ~10pt, leaving its final letter
  // legible in the export.
  const items = pt.runs.map((r) => ({
    x: r.x,
    y: r.y,
    width: r.w,
    height: r.h,
    length: stripItemSeams(r.c).length,
  }));
  const hasText = pt.runs.some((r) => r.c.trim().length > 0);
  return {
    // MuPDF pages are 0-based; PagePrepassEntry.page is 1-based (the reducer keys
    // pageTexts by it and the search saga iterates it 1..N).
    page: pt.page + 1,
    text,
    items,
    searchability: hasText ? 'searchable' : 'blank',
    dims: {
      width: pt.width > 0 ? pt.width : FALLBACK_PAGE_WIDTH,
      height: pt.height > 0 ? pt.height : FALLBACK_PAGE_HEIGHT,
    },
  };
}

/**
 * Backfill ONE page, for the render path when the streaming pre-pass hasn't
 * reached it yet (`page` is 1-based).
 *
 * Exists so that backfill uses the SAME engine and coordinate space as the
 * pre-pass. The render path used to fill the gap with pdf.js `getTextContent()`
 * instead, which is wrong twice over: pdf.js text-layer positions drift against
 * the MuPDF-rendered raster the user actually sees, and pdf.js items are
 * multi-character RUNS where MuPDF's are per character — so
 * `keywordRedactionsFromItems` had to interpolate a sub-run box with a uniform
 * `width / length`, which on a proportional font misses by a character or two.
 * A page that lost that race therefore carried silently misplaced geometry for
 * the whole session, for search highlights, keyword redactions and burn-in
 * alike, while a page that won it was exact. The gap dates from VE-27405,
 * which migrated this pre-pass to MuPDF without migrating the backfill that
 * fills the same field.
 *
 * Returns null when extraction fails; the caller should leave the page
 * without items rather than substitute geometry from another coordinate space.
 */
export async function extractSinglePagePrepass(
  pdfUrl: string,
  page: number
): Promise<PagePrepassEntry | null> {
  try {
    // Same retry as the streaming path, and for the same reason its docblock
    // gives: this fires during open-time churn, when an editor remount can
    // dispose the worker mid-request. This is the call site that most needs
    // the retry — it only runs when a page beat the pre-pass — and it was the
    // one skipping it.
    const result = await extractRangeWithRetry(
      pdfUrl,
      page - 1,
      page - 1,
      MUPDF_PRIORITY_HIGH
    );
    const pt = result.pages[0];
    if (!pt) {
      return null;
    }
    // The caller dispatches against its OWN page number, so a mismatch here
    // would write one page's geometry onto another — silently, and looking
    // exactly like the bug this function exists to fix.
    if (pt.page + 1 !== page) {
      return null;
    }
    return toPagePrepassEntry(pt);
  } catch {
    return null;
  }
}

/**
 * Extract text/geometry/dimensions for every page of the PDF at `pdfUrl`, and a
 * provisional searchability (`searchable` when the page has text, else `blank`
 * pending the post-batch scan probe).
 *
 * STREAMS pages in ranges of `PREPASS_RANGE_PAGES` as MuPDF extracts them, so
 * keyword search covers the indexed pages within seconds and the rail can show
 * progress — instead of one all-at-once batch minutes later on a long document
 * (VE-26267). The consumer accumulates the chunks for the searchability pass.
 * Best-effort: if the worker fails, streaming stops, and the return value
 * (`PrepassEnd`) says it failed. NOTE the render path's
 * backfill is NOT an independent safety net for that — since VE-27405 it goes
 * through this same MuPDF worker, so the two share a fault domain. A dead
 * worker means an unsearchable document, by design: the alternative was pdf.js
 * geometry in a MuPDF-rendered page, which misplaced redactions.
 */
export async function* extractPdfPagePrepass(
  pdfUrl: string
): AsyncGenerator<PrepassChunk, PrepassEnd, unknown> {
  let start = 0; // 0-based page index
  let total = 0; // learned from the first response
  for (;;) {
    const end = start + PREPASS_RANGE_PAGES - 1;
    let result: MupdfExtractResult;
    try {
      result = await extractRangeWithRetry(pdfUrl, start, end);
    } catch (err) {
      // Worker failure after retries — stop; whatever indexed so far stands, and
      // the render path retries the visible pages through the same worker.
      console.error('[pdfPrepass] MuPDF text extraction failed:', pdfUrl, err);
      return { failed: true };
    }
    total = result.totalPages;
    if (result.pages.length === 0) {
      return { failed: false }; // empty document, or start advanced past the last page
    }
    // Instrumented because this is the suspect: one object per GLYPH arrives
    // from the worker and is re-mapped twice here (pdfLike, then items).
    const chunk = timeSpan('prepass: glyphs→items (per chunk)', () =>
      result.pages.map(toPagePrepassEntry)
    );
    start += chunk.length;
    yield { chunk, indexed: Math.min(start, total), total };
    if (start >= total) {
      return { failed: false };
    }
    // One macrotask between flushes so the MuPDF page renders + React can
    // interleave on the main thread instead of the pre-pass hogging it.
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
}
